import React, { useEffect, useRef, useState } from 'react';
import './salaryGuidance.css';
import { getApiBaseUrl } from './apiBaseUrl';
import { buildAuthHeaders } from './authSession';

// Customer-facing guidance for a declined / not-yet-approved offer: what happened, in plain
// words, and what the customer can do about it right now. The backend keeps the precise rule
// codes and reviewer messages; this module only turns them into the two things a kiosk customer
// needs: a sentence they understand and a button that moves them forward.

export const GUIDANCE_ACTION = {
    LINK_ACCOUNT: 'LINK_ACCOUNT',
    UPLOAD_SLIP: 'UPLOAD_SLIP'
};

const BOTH_ACTIONS = [GUIDANCE_ACTION.LINK_ACCOUNT, GUIDANCE_ACTION.UPLOAD_SLIP];

const formatDisplayDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Why the salary engine found nothing in the linked account (backend salaryRejectionReason).
const SALARY_REASON_COPY = {
    ONLY_UPI_CREDITS: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'Only UPI credits were found',
            body: 'Money comes into this account by UPI. We can only verify salary that is paid by bank transfer (NEFT / IMPS). Link the account where your salary is credited, or upload your salary slip.'
        },
        hi: {
            title: 'खाते में सिर्फ UPI क्रेडिट मिले',
            body: 'इस खाते में पैसा UPI से आता है। हम सिर्फ बैंक ट्रांसफर (NEFT / IMPS) से आई सैलरी की पुष्टि कर सकते हैं। जिस खाते में सैलरी आती है उसे लिंक करें, या अपनी सैलरी स्लिप अपलोड करें।'
        }
    },
    SALARY_STALE: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'Salary credits have stopped',
            body: 'We found salary in this account, but the last credit was more than 45 days ago. If your salary now comes to another account, link that one, or upload your latest salary slip.'
        },
        hi: {
            title: 'सैलरी आना बंद हो गया है',
            body: 'इस खाते में सैलरी मिली, लेकिन आखिरी सैलरी 45 दिन से पहले आई थी। अगर अब सैलरी किसी और खाते में आती है तो वह खाता लिंक करें, या अपनी ताज़ा सैलरी स्लिप अपलोड करें।'
        }
    },
    AA_DATA_STALE: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'No recent activity in the linked account',
            body: 'The linked account has no recent transactions{date}. Please link the account where your salary is credited, or upload your salary slip.'
        },
        hi: {
            title: 'लिंक किए खाते में हाल का लेन-देन नहीं',
            body: 'लिंक किए खाते में हाल के लेन-देन नहीं हैं{date}। जिस खाते में सैलरी आती है उसे लिंक करें, या सैलरी स्लिप अपलोड करें।'
        }
    },
    INSUFFICIENT_SALARY_MONTHS: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'Not enough salary history yet',
            body: 'We need at least 2 consecutive monthly salary credits and found only one. Upload your last 2 salary slips, or link another account where earlier salary was credited.'
        },
        hi: {
            title: 'सैलरी का रिकॉर्ड अभी कम है',
            body: 'हमें लगातार 2 महीने की सैलरी क्रेडिट चाहिए, अभी सिर्फ 1 मिली है। पिछले 2 महीने की सैलरी स्लिप अपलोड करें, या वह खाता लिंक करें जिसमें पहले की सैलरी आई हो।'
        }
    },
    INCOME_OUTSIDE_DECLARED_BRACKET: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'Credits do not match the salary you declared',
            body: 'The regular credits in this account do not match the salary range and company you entered. Check those details and try again, or upload your salary slip.'
        },
        hi: {
            title: 'क्रेडिट आपकी बताई सैलरी से मेल नहीं खाते',
            body: 'इस खाते के नियमित क्रेडिट आपकी बताई सैलरी रेंज और कंपनी से मेल नहीं खाते। वे विवरण जांच कर दोबारा कोशिश करें, या सैलरी स्लिप अपलोड करें।'
        }
    },
    NO_TRANSACTIONS: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'No bank transactions were received',
            body: 'Your bank did not share any transactions for this account. Please link the account where your salary is credited, or upload your salary slip.'
        },
        hi: {
            title: 'बैंक से कोई लेन-देन नहीं मिला',
            body: 'आपके बैंक ने इस खाते का कोई लेन-देन साझा नहीं किया। जिस खाते में सैलरी आती है उसे लिंक करें, या सैलरी स्लिप अपलोड करें।'
        }
    },
    DEFAULT: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'We could not find a salary in this account',
            body: 'No regular salary credit from an employer was found in the linked account. Link the account where your salary is credited, or upload your salary slip.'
        },
        hi: {
            title: 'इस खाते में सैलरी नहीं मिली',
            body: 'लिंक किए खाते में किसी कंपनी से नियमित सैलरी क्रेडिट नहीं मिली। जिस खाते में सैलरी आती है उसे लिंक करें, या सैलरी स्लिप अपलोड करें।'
        }
    }
};
SALARY_REASON_COPY.NO_IDENTIFIABLE_PAYER = SALARY_REASON_COPY.DEFAULT;
SALARY_REASON_COPY.NO_LARGE_CREDITS = SALARY_REASON_COPY.DEFAULT;
SALARY_REASON_COPY.NO_SALARY_CREDIT_FOUND = SALARY_REASON_COPY.DEFAULT;

// Decline rules from the eligibility engine.
const RULE_COPY = {
    SP_005: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'We need 2 months of salary credits',
            body: 'We need at least 2 salary credits in the last 3 months and found {count}. Link the account where your salary is credited every month, or upload your last 2 salary slips.'
        },
        hi: {
            title: 'हमें 2 महीने की सैलरी क्रेडिट चाहिए',
            body: 'पिछले 3 महीनों में कम से कम 2 सैलरी क्रेडिट चाहिए, अभी {count} मिली। जिस खाते में हर महीने सैलरी आती है उसे लिंक करें, या पिछले 2 महीने की सैलरी स्लिप अपलोड करें।'
        }
    },
    SP_004: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'We could not confirm your salary',
            body: 'Your bank and bureau records did not show a regular salary. Link the account where your salary is credited, or upload your salary slip.'
        },
        hi: {
            title: 'सैलरी की पुष्टि नहीं हो पाई',
            body: 'बैंक और ब्यूरो रिकॉर्ड में नियमित सैलरी नहीं दिखी। जिस खाते में सैलरी आती है उसे लिंक करें, या सैलरी स्लिप अपलोड करें।'
        }
    },
    SP_001: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'Verified salary is below the minimum',
            body: 'The salary we could verify is below the minimum for this offer. If your actual salary is higher, upload your salary slip or link the account where your full salary is credited.'
        },
        hi: {
            title: 'पुष्टि हुई सैलरी न्यूनतम सीमा से कम है',
            body: 'जो सैलरी हम पुष्टि कर पाए वह इस ऑफर की न्यूनतम सीमा से कम है। अगर आपकी असली सैलरी इससे ज़्यादा है, तो सैलरी स्लिप अपलोड करें या वह खाता लिंक करें जिसमें पूरी सैलरी आती है।'
        }
    },
    SP_002: {
        actions: [],
        en: {
            title: 'An active loan was found',
            body: 'Your bureau report shows a loan that is still running, so we cannot offer this product right now. You can apply again once that loan is closed.'
        },
        hi: {
            title: 'एक चालू लोन मिला',
            body: 'आपकी ब्यूरो रिपोर्ट में एक लोन अभी चल रहा है, इसलिए अभी यह ऑफर नहीं दे सकते। वह लोन बंद होने के बाद दोबारा आवेदन करें।'
        }
    },
    SP_003: {
        actions: [],
        en: {
            title: 'A recent settlement was found',
            body: 'Your bureau report shows a loan settled recently, so we cannot offer this product right now.'
        },
        hi: {
            title: 'हाल का सेटलमेंट मिला',
            body: 'आपकी ब्यूरो रिपोर्ट में हाल में सेटल हुआ लोन दिख रहा है, इसलिए अभी यह ऑफर नहीं दे सकते।'
        }
    },
    NO_SEGMENT_MATCH: {
        actions: [],
        en: {
            title: 'Your profile did not match an offer',
            body: 'Based on your credit score and income we could not build an offer today. You can try again after a few weeks.'
        },
        hi: {
            title: 'प्रोफ़ाइल किसी ऑफर में फिट नहीं हुई',
            body: 'आपके क्रेडिट स्कोर और आय के आधार पर आज कोई ऑफर नहीं बन पाया। कुछ हफ्तों बाद दोबारा कोशिश करें।'
        }
    },
    AGE_ABOVE_LIMIT: {
        actions: [],
        en: { title: 'Age is outside the allowed range', body: 'We can only offer this loan within the allowed age range.' },
        hi: { title: 'उम्र तय सीमा से बाहर है', body: 'यह लोन सिर्फ तय उम्र सीमा के भीतर दिया जा सकता है।' }
    },
    FALLBACK_EVALUATION_ERROR: {
        actions: [],
        en: { title: 'We could not check your offer right now', body: 'Something went wrong on our side. Please try again in a few minutes.' },
        hi: { title: 'अभी ऑफर की जांच नहीं हो पाई', body: 'हमारी तरफ से कुछ गड़बड़ हुई। कुछ मिनट बाद दोबारा कोशिश करें।' }
    },
    BRE_PASS_DECLINE: {
        actions: [],
        en: { title: 'This application was not approved', body: 'Our team reviewed your application and could not approve it right now.' },
        hi: { title: 'यह आवेदन स्वीकृत नहीं हुआ', body: 'हमारी टीम ने आपका आवेदन देखा और अभी इसे स्वीकृत नहीं कर पाई।' }
    }
};
RULE_COPY.PR_INCOME_PATH = RULE_COPY.SP_004;
RULE_COPY.NTC_INCOME_PATH = RULE_COPY.SP_004;
// BRE v3 codes.
RULE_COPY.INCOME_PROOF_REQUIRED = RULE_COPY.SP_004;
RULE_COPY.AGE_OUT_OF_RANGE = RULE_COPY.AGE_ABOVE_LIMIT;
RULE_COPY.THIN_SALARY_REVIEW = {
    actions: BOTH_ACTIONS,
    en: {
        title: 'Our team is reviewing your application',
        body: 'We found only one salary credit in the linked account, so a manual check is needed. To speed it up, upload your last 2 salary slips or link the account where your salary is credited every month.'
    },
    hi: {
        title: 'हमारी टीम आपका आवेदन देख रही है',
        body: 'लिंक किए खाते में सिर्फ एक सैलरी क्रेडिट मिली, इसलिए मैन्युअल जांच ज़रूरी है। जल्दी के लिए पिछले 2 महीने की सैलरी स्लिप अपलोड करें, या वह खाता लिंक करें जिसमें हर महीने सैलरी आती है।'
    }
};
RULE_COPY.BUREAU_INCOME_REVIEW = {
    actions: BOTH_ACTIONS,
    en: {
        title: 'Our team is reviewing your application',
        body: 'Your income was estimated from your credit report, so a manual check is needed. To speed it up, upload your salary slip or link the account where your salary is credited.'
    },
    hi: {
        title: 'हमारी टीम आपका आवेदन देख रही है',
        body: 'आपकी आय क्रेडिट रिपोर्ट से अनुमानित की गई है, इसलिए मैन्युअल जांच ज़रूरी है। जल्दी के लिए सैलरी स्लिप अपलोड करें, या वह खाता लिंक करें जिसमें सैलरी आती है।'
    }
};
// Bureau-file declines (v3 global rules): same copy, no customer action can fix these today.
RULE_COPY.BUREAU_DECLINE = {
    actions: [],
    en: {
        title: 'Your credit report does not allow a loan right now',
        body: 'Your bureau report shows serious repayment problems on existing loans, such as written-off or heavily overdue accounts. Clear those dues and you can apply again after a few months.'
    },
    hi: {
        title: 'अभी आपकी क्रेडिट रिपोर्ट लोन की अनुमति नहीं देती',
        body: 'आपकी ब्यूरो रिपोर्ट में मौजूदा लोन पर गंभीर भुगतान समस्याएँ दिख रही हैं, जैसे written-off या बहुत ज़्यादा overdue खाते। वे बकाया चुकाकर कुछ महीनों बाद दोबारा आवेदन कर सकते हैं।'
    }
};
RULE_COPY.DPD_3M_OVER_60 = RULE_COPY.BUREAU_DECLINE;
RULE_COPY.LOSS_BUCKET_24M = RULE_COPY.BUREAU_DECLINE;
RULE_COPY.WRITE_OFF_2_PLUS = RULE_COPY.BUREAU_DECLINE;
RULE_COPY.OVERDUE_ACCOUNTS_OVER_5 = RULE_COPY.BUREAU_DECLINE;
RULE_COPY.PRIOR_LOAN_DPD_30 = {
    actions: [],
    en: {
        title: 'A previous loan was paid very late',
        body: 'One of your earlier loans with us ran more than 30 days past its due date in the last 6 months, so we cannot offer a new loan yet. You can apply again after 6 months of the closure.'
    },
    hi: {
        title: 'पिछला लोन बहुत देर से चुकाया गया',
        body: 'पिछले 6 महीनों में हमारे साथ आपका एक लोन तय तारीख से 30 दिन से ज़्यादा देर से चुका, इसलिए अभी नया लोन नहीं दे सकते। बंद होने के 6 महीने बाद दोबारा आवेदन करें।'
    }
};

const STATUS_COPY = {
    HOLD_INCOME_PROOF: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'We could not find your salary yet',
            body: 'No regular salary credit was found in the linked account. Upload your salary slip or link the account where your salary is credited, and your offer will be checked again.'
        },
        hi: {
            title: 'अभी आपकी सैलरी नहीं मिली',
            body: 'लिंक किए खाते में नियमित सैलरी क्रेडिट नहीं मिली। सैलरी स्लिप अपलोड करें या वह खाता लिंक करें जिसमें सैलरी आती है, फिर आपका ऑफर दोबारा जांचा जाएगा।'
        }
    },
    MANUAL_REVIEW: {
        actions: [],
        en: {
            title: 'Our team is reviewing your application',
            body: 'Your application needs a manual check. This usually takes one working day, and our team will contact you.'
        },
        hi: {
            title: 'हमारी टीम आपका आवेदन देख रही है',
            body: 'आपके आवेदन की मैन्युअल जांच हो रही है। इसमें आमतौर पर एक कार्य दिवस लगता है, हमारी टीम आपसे संपर्क करेगी।'
        }
    },
    INSUFFICIENT_DATA: {
        actions: BOTH_ACTIONS,
        en: {
            title: 'We need a little more information',
            body: 'We could not verify enough income to show a final offer. Link the account where your salary is credited, or upload your salary slip.'
        },
        hi: {
            title: 'हमें थोड़ी और जानकारी चाहिए',
            body: 'अंतिम ऑफर दिखाने लायक आय की पुष्टि नहीं हो पाई। जिस खाते में सैलरी आती है उसे लिंक करें, या सैलरी स्लिप अपलोड करें।'
        }
    }
};

const GENERIC_COPY = {
    actions: [],
    en: { title: 'We could not prepare an offer right now', body: 'Our team will reach out to you soon.' },
    hi: { title: 'अभी ऑफर तैयार नहीं हो पाया', body: 'हमारी टीम जल्द ही आपसे संपर्क करेगी।' }
};

const fillTemplate = (text, values) => String(text || '').replace(/\{(\w+)\}/g, (_match, key) => (values[key] ?? ''));

export function getOfferGuidance(offer, isHindi = false) {
    if (!offer || typeof offer !== 'object') return null;
    const ruleCode = String(offer.ruleCode || '').trim().toUpperCase();
    const decisionStatus = String(offer.decisionStatus || '').trim().toUpperCase();
    const approved = Boolean(offer.approved || offer.isEligible) && Number(offer.offeredAmount || offer.amount || 0) > 0;
    if (approved) return null;
    if (ruleCode === 'AA_PENDING' || decisionStatus === 'PENDING') return null;

    const salaryReason = String(offer.salaryRejectionReason || '').trim().toUpperCase();
    let entry = null;
    if (ruleCode === 'SALARY_NOT_FOUND' || (salaryReason && !RULE_COPY[ruleCode])) {
        entry = SALARY_REASON_COPY[salaryReason] || SALARY_REASON_COPY.DEFAULT;
    } else if (RULE_COPY[ruleCode]) {
        entry = RULE_COPY[ruleCode];
    } else if (STATUS_COPY[decisionStatus]) {
        entry = STATUS_COPY[decisionStatus];
    }

    const lang = isHindi ? 'hi' : 'en';
    const newestDate = formatDisplayDate(offer.aaNewestTransactionDate);
    const values = {
        date: newestDate ? (isHindi ? ` (आखिरी लेन-देन ${newestDate})` : ` (last one on ${newestDate})`) : '',
        count: Number.isFinite(Number(offer.salaryCreditCountLast3Months)) && offer.salaryCreditCountLast3Months != null
            ? String(offer.salaryCreditCountLast3Months)
            : (isHindi ? 'कम' : 'fewer')
    };

    if (!entry) {
        const backendReason = String(offer.displayReason || offer.reason || '').trim();
        return {
            ruleCode,
            tone: 'info',
            title: GENERIC_COPY[lang].title,
            body: backendReason || GENERIC_COPY[lang].body,
            actions: []
        };
    }

    return {
        ruleCode,
        tone: entry.actions.length ? 'action' : 'info',
        title: entry[lang].title,
        body: fillTemplate(entry[lang].body, values),
        actions: entry.actions
    };
}

const readStoredMobile = () => {
    try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        return String(stored.mobile_number || stored.mobile || '').replace(/\D/g, '').slice(-10);
    } catch (_error) {
        return '';
    }
};

export async function fetchSalarySlipStatus(mobile) {
    const resolvedMobile = String(mobile || readStoredMobile()).replace(/\D/g, '').slice(-10);
    if (!resolvedMobile) return null;
    const response = await fetch(`${getApiBaseUrl()}/api/salary-slip/status?mobile=${encodeURIComponent(resolvedMobile)}`, {
        headers: buildAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) return null;
    return { ...(data.upload || { status: 'NONE' }), uploadsEnabled: data.uploadsEnabled !== false };
}

export async function uploadSalarySlip({ mobile, file, password = '' }) {
    const resolvedMobile = String(mobile || readStoredMobile()).replace(/\D/g, '').slice(-10);
    const form = new FormData();
    form.append('mobile', resolvedMobile);
    if (password) form.append('password', password);
    form.append('file', file, file.name || 'salary-slip.pdf');
    const response = await fetch(`${getApiBaseUrl()}/api/salary-slip/upload`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: form
    });
    const data = await response.json().catch(() => ({}));
    return { httpStatus: response.status, ...data };
}

const slipStatusCopy = (slipStatus, isHindi) => {
    const status = String(slipStatus?.status || 'NONE').toUpperCase();
    const amount = Number(slipStatus?.salaryAmount || 0);
    if (status === 'VERIFIED') {
        return isHindi
            ? `सैलरी स्लिप सत्यापित${amount > 0 ? `: ₹${amount.toLocaleString('en-IN')}` : ''}। आपका ऑफर इसी के आधार पर बनेगा।`
            : `Salary slip verified${amount > 0 ? `: ₹${amount.toLocaleString('en-IN')}` : ''}. Your offer will be based on it.`;
    }
    if (status === 'UNDER_REVIEW') {
        return isHindi
            ? 'सैलरी स्लिप मिल गई है। हमारी टीम एक कार्य दिवस में इसकी जांच करेगी।'
            : 'Salary slip received. Our team will verify it within one working day.';
    }
    if (status === 'REJECTED') {
        return isHindi
            ? 'पिछली सैलरी स्लिप स्वीकार नहीं हुई। कृपया साफ, असली PDF अपलोड करें।'
            : 'The last salary slip was not accepted. Please upload a clear, original PDF.';
    }
    return '';
};

export function SalaryGuidanceCard({
    offer,
    isHindi = false,
    fallbackReason = '',
    slipStatus = null,
    compact = false,
    onLinkAccount,
    onUploadSlip
}) {
    const guidance = getOfferGuidance(offer, isHindi);
    if (!guidance && !fallbackReason) return null;
    const title = guidance?.title || (isHindi ? 'अभी ऑफर तैयार नहीं हो पाया' : 'We could not prepare an offer right now');
    const body = guidance?.body || fallbackReason;
    const actions = guidance?.actions || [];
    const uploadsEnabled = slipStatus?.uploadsEnabled !== false;
    const statusLine = slipStatusCopy(slipStatus, isHindi);
    const showLink = actions.includes(GUIDANCE_ACTION.LINK_ACCOUNT) && typeof onLinkAccount === 'function';
    const showUpload = actions.includes(GUIDANCE_ACTION.UPLOAD_SLIP) && typeof onUploadSlip === 'function' && uploadsEnabled;

    return (
        <section className={`sg-card sg-card--${guidance?.tone || 'info'} ${compact ? 'sg-card--compact' : ''}`} aria-live="polite">
            <p className="sg-kicker">{isHindi ? 'ऑफर क्यों नहीं बना' : 'Why there is no offer yet'}</p>
            <h3 className="sg-title">{title}</h3>
            <p className="sg-body">{body}</p>
            {statusLine && <p className="sg-status">{statusLine}</p>}
            {(showLink || showUpload) && (
                <div className="sg-actions">
                    {showLink && (
                        <button type="button" className="sg-btn sg-btn--primary" onClick={onLinkAccount}>
                            {isHindi ? 'दूसरा बैंक खाता लिंक करें' : 'Link another bank account'}
                        </button>
                    )}
                    {showUpload && (
                        <button type="button" className="sg-btn sg-btn--secondary" onClick={onUploadSlip}>
                            {isHindi ? 'सैलरी स्लिप अपलोड करें' : 'Upload salary slip'}
                        </button>
                    )}
                </div>
            )}
            {(showLink || showUpload) && (
                <p className="sg-note">
                    {isHindi
                        ? 'सैलरी स्लिप PDF में होनी चाहिए, जैसी कंपनी या HR पोर्टल से मिलती है।'
                        : 'The salary slip must be the PDF you get from your company or HR portal.'}
                </p>
            )}
        </section>
    );
}

const AA_STAGES = [
    { id: 'consent', en: 'Consent received', hi: 'बैंक की सहमति मिली' },
    { id: 'statement', en: 'Fetching your bank statement', hi: 'बैंक स्टेटमेंट लिया जा रहा है' },
    { id: 'salary', en: 'Checking salary credits', hi: 'सैलरी क्रेडिट की जांच हो रही है' }
];

const stageIndexForPhase = (phase) => {
    const normalized = String(phase || '').trim().toUpperCase();
    if (normalized === 'VERIFIED') return 2;
    if (normalized === 'CONSENT_ACTIVE' || normalized === 'DATA_PENDING') return 1;
    return 0;
};

const formatElapsed = (seconds) => {
    const safe = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(safe / 60);
    const rest = safe % 60;
    return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

export function AaProgressCard({ aaPhase, isHindi = false, startedAt = null, onRefresh, refreshing = false }) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    const activeIndex = stageIndexForPhase(aaPhase);
    const elapsedSeconds = startedAt ? (now - startedAt) / 1000 : 0;
    const takingLong = elapsedSeconds >= 90;

    return (
        <section className="sg-card sg-card--pending" aria-live="polite">
            <p className="sg-kicker">{isHindi ? 'बैंक सत्यापन चल रहा है' : 'Bank verification in progress'}</p>
            <h3 className="sg-title">{isHindi ? 'आपका ऑफर तैयार हो रहा है' : 'Your offer is being prepared'}</h3>
            <ol className="sg-steps">
                {AA_STAGES.map((stage, index) => {
                    const state = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'todo';
                    return (
                        <li key={stage.id} className={`sg-step sg-step--${state}`}>
                            <span className="sg-step-dot" aria-hidden="true">{state === 'done' ? '✓' : index + 1}</span>
                            <span>{isHindi ? stage.hi : stage.en}</span>
                        </li>
                    );
                })}
            </ol>
            <p className="sg-body">
                {takingLong
                    ? (isHindi
                        ? 'इसमें सामान्य से ज़्यादा समय लग रहा है। आपकी प्रगति सेव है, आप बाद में लॉगिन करके यहीं से आगे बढ़ सकते हैं।'
                        : 'This is taking longer than usual. Your progress is saved, so you can log in later and continue from here.')
                    : (isHindi
                        ? 'आमतौर पर 1 से 2 मिनट लगते हैं। कृपया यह स्क्रीन खुली रखें।'
                        : 'This usually takes 1 to 2 minutes. Please keep this screen open.')}
            </p>
            <div className="sg-actions sg-actions--inline">
                {startedAt && <span className="sg-timer">{formatElapsed(elapsedSeconds)}</span>}
                {typeof onRefresh === 'function' && (
                    <button type="button" className="sg-btn sg-btn--secondary" onClick={onRefresh} disabled={refreshing}>
                        {refreshing ? (isHindi ? 'जांच रहे हैं…' : 'Checking…') : (isHindi ? 'अभी जांचें' : 'Check now')}
                    </button>
                )}
            </div>
        </section>
    );
}

export function SalarySlipUploadModal({ open, mobile = '', isHindi = false, onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [password, setPassword] = useState('');
    const [needsPassword, setNeedsPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setFile(null);
        setPassword('');
        setNeedsPassword(false);
        setBusy(false);
        setError('');
        setResult(null);
    }, [open]);

    if (!open) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!file) {
            setError(isHindi ? 'पहले PDF फ़ाइल चुनें।' : 'Please choose a PDF file first.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const response = await uploadSalarySlip({ mobile, file, password });
            if (response?.success) {
                const status = response.underReview
                    ? { status: 'UNDER_REVIEW', salaryAmount: null, uploadsEnabled: true }
                    : { status: 'VERIFIED', salaryAmount: response.salaryAmount || null, uploadsEnabled: true };
                setResult(status);
                if (typeof onUploaded === 'function') onUploaded(status);
                return;
            }
            if (response?.needsPassword) {
                setNeedsPassword(true);
                setError(response.wrongPassword
                    ? (isHindi ? 'पासवर्ड गलत है। दोबारा डालें।' : 'That password is incorrect. Please try again.')
                    : (isHindi ? 'यह PDF पासवर्ड से सुरक्षित है। पासवर्ड डालें।' : 'This PDF is password-protected. Enter its password.'));
                return;
            }
            setError(response?.message || (isHindi ? 'अपलोड नहीं हो पाया। दोबारा कोशिश करें।' : 'Upload failed. Please try again.'));
        } catch (_error) {
            setError(isHindi ? 'नेटवर्क में समस्या है। दोबारा कोशिश करें।' : 'Network problem. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="sg-overlay" role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}>
            <form className="sg-modal" onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
                <h3 className="sg-title">{isHindi ? 'सैलरी स्लिप अपलोड करें' : 'Upload your salary slip'}</h3>
                {result ? (
                    <>
                        <p className="sg-body sg-body--success">{slipStatusCopy(result, isHindi)}</p>
                        <div className="sg-actions">
                            <button type="button" className="sg-btn sg-btn--primary" onClick={onClose}>
                                {isHindi ? 'ठीक है' : 'Done'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="sg-body">
                            {isHindi
                                ? 'पिछले महीने की सैलरी स्लिप PDF में चुनें। हम उसमें से सैलरी पढ़कर आपका ऑफर तुरंत अपडेट करेंगे।'
                                : 'Choose last month\'s salary slip as a PDF. We read the salary from it and update your offer right away.'}
                        </p>
                        <label className="sg-file">
                            <input
                                ref={inputRef}
                                type="file"
                                accept="application/pdf,.pdf"
                                onChange={(event) => {
                                    const next = event.target.files?.[0] || null;
                                    setFile(next);
                                    setError('');
                                }}
                                disabled={busy}
                            />
                            <span className="sg-file-label">
                                {file ? file.name : (isHindi ? 'PDF फ़ाइल चुनें' : 'Choose PDF file')}
                            </span>
                        </label>
                        {needsPassword && (
                            <label className="sg-field">
                                <span>{isHindi ? 'PDF का पासवर्ड' : 'PDF password'}</span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    autoComplete="off"
                                    disabled={busy}
                                />
                            </label>
                        )}
                        {error && <p className="sg-error">{error}</p>}
                        <div className="sg-actions">
                            <button type="button" className="sg-btn sg-btn--secondary" onClick={onClose} disabled={busy}>
                                {isHindi ? 'रद्द करें' : 'Cancel'}
                            </button>
                            <button type="submit" className="sg-btn sg-btn--primary" disabled={busy || !file}>
                                {busy ? (isHindi ? 'अपलोड हो रहा है…' : 'Uploading…') : (isHindi ? 'अपलोड करें' : 'Upload')}
                            </button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
}
